import {FormArray, FormControl, FormGroup} from '@angular/forms';

import {of} from 'rxjs/internal/observable/of';
import {map, switchMap} from 'rxjs/operators';

import {InArrayValidator, RequiredValidatorKey} from '@spa/validators';

import {
    ApiError,
    ApiResponse,
    ServerResponse,
    ServerResponseData,
    HttpStatusCodesEnum,
    TradingService,
    TradingAccountAccessInterface,
    TradingAccountAccessResponseInterface,
    PermissionModel,
    TradingAccountAccessModel,
    TradingAccountAccessInputModel
} from '@spa/api';

import {PublishPortfolioRightRule} from '../classes';
import {Helper} from '../../../shared/classes';
import {CheckboxControl} from '../../../shared/classes/controls';
import {ColorCodesEnum, ComponentTypesEnum} from '../../../shared/enums/common';
import {AutocompleteOptionInterface} from '../../../shared/components/autocomplete';

import {AssignAccountRightsSelectableEntityInterface} from '../interfaces';
import {TradingAccountTypesEnum, AssignClientAccountRightsFormErrorStatesEnum} from '../enums';

import {
    AssignClientAccountRightsLabel,
    AssignClientAccountRightsFormItems,
    AssignClientAccountRightsTypeSelector,
    AssignClientAccountRightsArraySelector,
    AssignClientAccountRightsFormInterface,
    AssignClientAccountRightsAccountOrUserSelector
} from '../../../../assets/descriptors';

export class AssignClientAccountRightsForm {
    private readonly tradingService: TradingService;

    public readonly initForAccount: boolean;

    public form: FormGroup;
    public rightsErrors: string[];
    public controls: AssignClientAccountRightsFormInterface;
    public assignClientAccountRightsArraySelector: string;

    constructor(tradingService: TradingService, initForAccount: boolean) {
        this.tradingService = tradingService;

        this.rightsErrors = [];
        this.assignClientAccountRightsArraySelector = AssignClientAccountRightsArraySelector;
        /**
         * Use form with dialog, which is opened from PanelAccountsEditComponent
         */
        this.initForAccount = initForAccount;
    }

    private initAccountControlOptions(
        accountType: number,
        selectableEntities: AssignAccountRightsSelectableEntityInterface[]
    ): AutocompleteOptionInterface[] {
        let entities = selectableEntities;

        if (!this.initForAccount) {
            entities = entities
                .filter((entity: AssignAccountRightsSelectableEntityInterface) => entity.accountType === accountType);
        }

        return entities.map((item: AssignAccountRightsSelectableEntityInterface) => {
            return {
                label: item.name,
                value: item.id
            };
        });
    }

    private initTradingAccountRightsCheckboxes(permissions: PermissionModel[]): CheckboxControl[] {
        return permissions.filter((permission: PermissionModel) => {
            // The publish-portfolio right is granted on the owner's existing row only, never while assigning a new row.
            return Helper.enumsHelper().permissionsEnumHelper()
                .getPortalTradingAccountPermissions().indexOf(permission.permissionString) >= 0
                && !PublishPortfolioRightRule.isPublishRight(permission.permissionString);
        }).map((permission: PermissionModel, index: number) => {
            return new CheckboxControl({
                name: index + '',
                value: permission.id,
                placeholder: Helper.textsHelper().getPortalTradingAccountPermissionName(permission.permissionString),
                validators: [],
                componentType: ComponentTypesEnum.Checkbox,
                checked: false,
                color: ColorCodesEnum.Primary,
                expectBooleanValue: false
            });
        });
    }

    private initCashAccountRightsCheckboxes(permissions: PermissionModel[]): CheckboxControl[] {
        return permissions.filter((permission: PermissionModel) => {
            return Helper.enumsHelper().permissionsEnumHelper()
                .getPortalCashAccountPermissions().indexOf(permission.permissionString) >= 0;
        }).map((permission: PermissionModel, index: number) => {
            return new CheckboxControl({
                name: index + '',
                value: permission.id,
                placeholder: Helper.textsHelper().getPortalCashAccountPermissionName(permission.permissionString),
                validators: [],
                componentType: ComponentTypesEnum.Checkbox,
                checked: false,
                color: ColorCodesEnum.Primary,
                expectBooleanValue: false
            });
        });
    }

    private assignRights(userOrAccountId: number, userRelationType: number): ServerResponse<boolean> {
        const entityId = this.form.value[AssignClientAccountRightsAccountOrUserSelector];

        const permissionIds = this.form.get(AssignClientAccountRightsArraySelector).value
            .filter((id: number) => (id !== null));

        const inputModel = new TradingAccountAccessInputModel(
            !this.initForAccount ? userOrAccountId : entityId,
            userRelationType,
            !this.initForAccount ? entityId : userOrAccountId,
            permissionIds
        );

        return this.tradingService.assignAccountAccessRight(inputModel).pipe(
            map((response: ServerResponseData<TradingAccountAccessResponseInterface>) => {
                if (response.status !== HttpStatusCodesEnum.Ok) {
                    return Helper.errorsHelper().createErrorResponse<TradingAccountAccessResponseInterface>(
                        response,
                        AssignClientAccountRightsFormErrorStatesEnum.AssignRightsFailed
                    );
                }

                return Helper.createSuccessResponse<boolean>(true);
            })
        );
    }

    private assignRightsIfNotExists(accountId: number, userRelationType: number): ServerResponse<boolean> {
        const entityId = this.form.value[AssignClientAccountRightsAccountOrUserSelector];

        return this.tradingService.getAccountsAccessRights(entityId, userRelationType).pipe(
            switchMap((response: ServerResponseData<TradingAccountAccessInterface[]>) => {
                if (response.status !== HttpStatusCodesEnum.Ok) {
                    return Helper.errorsHelper().createErrorResponseObservable<TradingAccountAccessInterface[]>(
                        response,
                        AssignClientAccountRightsFormErrorStatesEnum.CheckAlreadyAssignedRightsFailed
                    );
                }

                if (response.data === null) {
                    return this.assignRights(accountId, userRelationType);
                }

                if (!Array.isArray(response.data)) {
                    const errorResponseForInvalidData = Helper.errorsHelper().createResponseForInvalidData(
                        AssignClientAccountRightsFormErrorStatesEnum.CheckAlreadyAssignedRightsFailed
                    );

                    return of(errorResponseForInvalidData);
                }

                const alreadyAssignedRightsToCurrentAccount = (response.data as TradingAccountAccessInterface[])
                    .map((item: TradingAccountAccessInterface) => new TradingAccountAccessModel(item))
                    .filter((item: TradingAccountAccessModel) => item.accountId === accountId);

                if (alreadyAssignedRightsToCurrentAccount.length > 0) {
                    const error = new ApiError(AssignClientAccountRightsFormErrorStatesEnum.AccessRightsAlreadyAssigned);
                    const errorResponse = new ApiResponse(HttpStatusCodesEnum.BadRequest, [error]);

                    return of(errorResponse);
                }

                return this.assignRights(accountId, userRelationType);
            })
        );
    }

    initForm(
        accountType: number,
        selectableEntities: AssignAccountRightsSelectableEntityInterface[],
        permissions: PermissionModel[]
    ): void {
        this.form = new FormGroup({});
        this.controls = AssignClientAccountRightsFormItems(accountType, this.initForAccount);

        if (this.initForAccount) {
            delete this.controls.type;
        }

        const accountIds = selectableEntities
            .map((entity: AssignAccountRightsSelectableEntityInterface) => entity.id);

        this.controls.selectableEntity.extendValidators(InArrayValidator(accountIds));
        this.controls.selectableEntity.options.splice(0, this.controls.selectableEntity.options.length);

        this.controls.selectableEntity.options.push(...this.initAccountControlOptions(accountType, selectableEntities));

        if (accountType !== TradingAccountTypesEnum.Cash) {
            this.controls.rights = this.initTradingAccountRightsCheckboxes(permissions);
        } else {
            this.controls.rights = this.initCashAccountRightsCheckboxes(permissions);
        }

        const rightsFormArray = new FormArray([]);

        const nonIterableControlNames = [
            AssignClientAccountRightsTypeSelector,
            AssignClientAccountRightsAccountOrUserSelector
        ];

        Object.keys(this.controls).forEach((formItemKey: string) => {
            const control = this.controls[formItemKey];

            if (nonIterableControlNames.indexOf(control.name) < 0) {
                control.forEach((checkbox: CheckboxControl) => {
                    const checkboxFormControl = new FormControl(checkbox.value, checkbox.validators);

                    rightsFormArray.push(checkboxFormControl);
                });
            } else {
                this.form.addControl(control.name, new FormControl(control.value, control.validators));
            }
        });

        this.form.addControl(AssignClientAccountRightsArraySelector, rightsFormArray);
    }

    validateForm(messageFunction: (controlLabel?: string) => { [key: string]: string; }): boolean {
        let isInvalidForm = false;

        if (this.form.invalid) {
            Object.keys(this.controls).forEach((controlKey: string) => {
                const controlName = this.controls[controlKey].name;
                const formControl = this.form.get(controlName);

                if (formControl && formControl.invalid) {
                    const errors = Object.keys(formControl.errors).map((errorKey: string) => {
                        return messageFunction(this.controls[controlKey].placeholder)[errorKey];
                    });

                    this.controls[controlKey].setErrors(errors);
                }
            });

            isInvalidForm = true;
        }

        const rightsFormControl = this.form.get(AssignClientAccountRightsArraySelector) as FormArray;

        if (!rightsFormControl) {
            isInvalidForm = true;
        } else {
            const rightIds = rightsFormControl.value.filter((item: number) => item !== null);

            if (rightIds.length === 0) {
                this.rightsErrors.splice(0, this.rightsErrors.length);
                this.rightsErrors.push(messageFunction(AssignClientAccountRightsLabel)[RequiredValidatorKey]);

                isInvalidForm = true;
            }
        }

        return !isInvalidForm;
    }

    createRequest(userOrAccountId: number, userRelationType: number): ServerResponse<boolean> {
        if (!this.initForAccount) {
            return this.assignRights(userOrAccountId, userRelationType);
        }

        return this.assignRightsIfNotExists(userOrAccountId, userRelationType);
    }
}
